import { Info, Eye, EyeSlash } from '@phosphor-icons/react';
import { useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Tooltip from '../Tooltip';
import { normalizeEmail } from '@/shared/utils/emailDomain.util';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
	name: string;
	type: string;
	placeholder?: string;
	sx?: string;
	/** Clases para el INPUT interno (sx va al wrapper) — p.ej. bg-white cuando
	 * el bg-transparent del shadcn deja ver un fondo degradado (login). */
	inputSx?: string;
	label?: string;
	errorMessage?: string;
	autofocus?: boolean;
	rules?: object;
	multiline?: boolean;
	minRows?: number;
	maxLength?: number;
	showValueUpperCase?: boolean;
	control?: any;
	isDarkTheme?: boolean;
	showTooltip?: boolean;
	tooltipMessage?: string;
	size?: 'small' | 'medium';
	disabled?: boolean;
	borderRadius?: string;
	onFocus?: () => void;
	// Optional sanitizer applied to the raw input on every change before it
	// reaches react-hook-form — e.g. uppercase + strip spaces for RFC.
	transform?: (value: string) => string;
	/** Slot a la DERECHA del campo, en su misma fila (p.ej. un botón de ayuda).
	 * Vive dentro de la fila del input —no envolviendo al componente— para que
	 * la etiqueta arriba y el mensaje de error abajo no lo desalineen. */
	fieldAction?: ReactNode;
}

const Input = ({
	name,
	type,
	placeholder = 'Escribe aquí',
	sx = '',
	inputSx = '',
	label = '',
	errorMessage,
	autofocus,
	control,
	rules,
	multiline = false,
	minRows = 0,
	maxLength,
	showValueUpperCase = false,
	isDarkTheme = false,
	showTooltip,
	tooltipMessage,
	size = 'medium',
	disabled = false,
	borderRadius = '10px',
	onFocus,
	transform,
	fieldAction,
}: Props) => {
	const [showPassword, setShowPassword] = useState(false);
	const { control: defaultControl } = useForm();

	return (
		<div className={`${isDarkTheme && 'customInput'} relative w-full ${sx}`}>
			<Controller
				rules={rules}
				control={control || defaultControl}
				name={name}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<div className='w-full'>
						{label && (
							<Label
								className={`mb-1.5 block text-sm ${
									(!!error || !!errorMessage)
										? 'text-red-500'
										: isDarkTheme
											? 'text-white'
											: ''
								} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
							>
								{label}
							</Label>
						)}

						<div className='flex items-stretch gap-2'>
						<div className='relative flex-1'>
							{multiline ? (
								<textarea
									autoFocus={autofocus}
									onChange={onChange}
									onFocus={onFocus}
									value={showValueUpperCase ? value?.toUpperCase() : value}
									placeholder={placeholder}
									maxLength={maxLength}
									disabled={disabled}
									rows={minRows || 3}
									className={`flex w-full rounded-md border bg-transparent px-3 py-2 text-base md:text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none ${
										(!!error || !!errorMessage) ? 'border-red-500' : 'border-input'
									} ${isDarkTheme ? 'text-white border-white/30' : ''}`}
									style={{ borderRadius }}
								/>
							) : (
								<ShadcnInput
									autoFocus={autofocus}
									onChange={(e) => {
										if (type === 'tel') {
											e.target.value = e.target.value.replace(/\D/g, '');
										} else if (type === 'email') {
											e.target.value = normalizeEmail(e.target.value);
										} else if (transform) {
											e.target.value = transform(e.target.value);
										}
										onChange(e);
									}}
									onFocus={onFocus}
									value={showValueUpperCase ? value?.toUpperCase() : value}
									placeholder={placeholder}
									maxLength={maxLength}
									autoCapitalize={type === 'email' ? 'none' : undefined}
									autoComplete={type === 'password' ? 'new-password' : 'off'}
									disabled={disabled}
									type={
										type === 'password'
											? showPassword
												? 'text'
												: 'password'
											: type
									}
									className={`${size === 'small' ? 'h-9' : 'h-10'} ${
										(!!error || !!errorMessage) ? 'border-red-500' : ''
									} ${isDarkTheme ? 'text-white border-white/30' : ''} ${
										(type === 'password' || showTooltip) ? 'pr-10' : ''
									} ${inputSx}`}
									style={{ borderRadius }}
								/>
							)}

							{type === 'password' && (
								<button
									type='button'
									aria-label={
										showPassword
											? 'Ocultar contraseña'
											: 'Mostrar contraseña'
									}
									onClick={() =>
										!disabled && setShowPassword(!showPassword)
									}
									disabled={disabled}
									className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 opacity-70 ${
										isDarkTheme ? 'text-white' : ''
									}`}
								>
									{!showPassword ? (
										<Eye
											size={22}
											weight='bold'
										/>
									) : (
										<EyeSlash
											size={22}
											weight='bold'
										/>
									)}
								</button>
							)}
							{showTooltip && (
								<div className='absolute right-2 top-1/2 -translate-y-1/2'>
									<Tooltip message={tooltipMessage || ''}>
										<button
											type='button'
											aria-label='tooltip label'
											disabled={disabled}
											className='p-1'
										>
											<Info size={18} />
										</button>
									</Tooltip>
								</div>
							)}
						</div>
						{fieldAction}
						</div>
						{(errorMessage || error) && (
							<p className='text-sm text-red-500 mt-1'>
								{errorMessage ? errorMessage : error ? error.message : null}
							</p>
						)}
					</div>
				)}
			/>
		</div>
	);
};

export default Input;
